import {
  JangjiQuotationRequestProps,
  QuotationRequestBody,
  SangjoComparisonRequestProps,
} from "@/types/quotationType";
import { Axios } from "@/utils/axios";

export const getSangjoQuotationRequestExists = async () => {
  const { data } = await Axios.get(`/sangjo_quotation_requests/exists/`);
  return data;
};

const getSangjoQuotationStatus = (status: string) => {
  if (status === "1주에서 한 달 정도 기간이 남은 것 같아요.") {
    return "1달 정도 기간이 남은 것 같아요.";
  }

  if (status === "임종이 며칠 남지 않았어요.") {
    return "임종이 얼마 남지 않았어요.";
  }

  return status;
};

export interface QuotationResponse {
  id: number;
  status: string;
  funeral_sido: string;
  funeral_sigungu: string;
  needs_binso: boolean;
  condolers: string;
  initial_type: string;
  is_sequential: boolean;
  sangjo_comparison_id: null | number;
}

/**
 * 견적 요청 제출. 서버가 토큰 유무로 회원/비회원을 판정하므로 프론트는 분기하지 않는다.
 * - 비회원: 항상 create (201, user=null orphan)
 * - 회원 + 기존 견적 없음: create (201)
 * - 회원 + 기존 견적 있음: in-place merge (200). 409 없음
 *
 * 응답 `id`는 Request id
 */
export const submitSangjoQuotationRequest = async (body: Readonly<QuotationRequestBody>) => {
  return Axios.post<QuotationResponse>(`/sangjo_quotation_requests/v2/`, {
    ...body,
    status: getSangjoQuotationStatus(body.status),
  });
};

export const postJangjiRecommendRequest = async (body: JangjiQuotationRequestProps) => {
  const response = await Axios.post(`/jangji_recommend_requests/`, body);
  return response;
};

export const postParlorRecommendationRequest = async ({
  quotationRequestId,
  body,
}: {
  quotationRequestId: number;
  body: {
    desired_type: string;
    most_important_factor: string;
  };
}) => {
  const response = await Axios.post(
    `/sangjo_quotation_requests/${quotationRequestId}/parlor_recommendation/`,
    body,
  );
  return response;
};

export const patchSangjoQuotationRequestAcceptNightCall = async (
  id: string,
  accept_night_call: boolean,
) => {
  const response = await Axios.patch(`/sangjo_quotation_requests/${id}/`, {
    accept_night_call,
  });

  return response;
};

/** 야간 여유 견적 — 상담 희망 시간 저장 (O2O upsert) */
export const patchSangjoQuotationRequestConsultTime = async (
  id: number | string,
  body: { start: string; end: string }, // "09:00", "10:00"
) => {
  return Axios.patch(`/sangjo_quotation_requests/${id}/consult-time/`, body);
};

export const getSangjoQuotations = async () => {
  const { data } = await Axios.get(`/sangjo_quotations/`);
  return data;
};

export const getSangjoQuotation = async (id: string) => {
  const { data } = await Axios.get(`/sangjo_quotations/${id}/`);
  return data;
};

/**
 * 로그인 유저의 견적서를 id 없이 조회한다. 응답은 `getSangjoQuotation`과 동일 스키마.
 * 실패는 세 가지다. 401(비로그인) / 404(견적 요청 없음) / 500(견적서 생성 실패).
 */
export const getSangjoQuotationMe = async () => {
  const { data } = await Axios.get(`/sangjo_quotations/me/`);
  return data;
};

export const postSangjoQuotationRequestQuotation = async (id: string) => {
  const response = await Axios.post(`/sangjo_quotation_requests/${id}/quotation/`);
  return response;
};

export const postSangjoQuotationConsultReservation = async (
  id: string,
  body: {
    need_now: boolean;
    timeslot: string;
    postscript?: string;
  },
) => {
  return await Axios.post(`/sangjo_quotations/${id}/consult_reservation/`, body);
};

export const patchSangjoComparisonsDetail = async (
  id: number,
  body: SangjoComparisonRequestProps,
) => {
  const response = await Axios.patch(`/sangjo_comparisons/${id}/`, body);
  return response;
};

interface BenefitProps {
  quotation: number;
  content: string;
}

export const postSangjoQuotationBenefit = async (body: BenefitProps) => {
  const { data } = await Axios.post(`/benefits/`, body);
  return data;
};
export const patchSangjoQuotationBenefit = async (id: number, body: BenefitProps) => {
  const { data } = await Axios.patch(`/benefits/${id}/`, body);
  return data;
};
